import { useRef, useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { createRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserPlus, X } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Route as RootRoute, requireStaffManagementRoute } from "./__root";
import { useAuth } from "../providers/auth-context";
import { useAdminApi } from "../providers/api-context";
import type { UserDto, CreatedInviteDto, VehicleDto } from "@carry/contract";
import { isDriverCapableRole } from "@carry/shared";
import {
  ConfirmDeleteDialog,
  Field,
  FormDialog,
  MastersDataTable,
  MastersShell,
  RowActionButtons,
  mastersValidateSearch,
  useMastersQ,
} from "./_masters";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@carry/ui";
import { formatDateTime } from "../lib/format";
import { roleLabel } from "../lib/role-label";
import { copyToClipboard } from "../lib/clipboard";
import { formatApiError } from "../lib/api-error";

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/masters/users",
  validateSearch: mastersValidateSearch,
  beforeLoad: requireStaffManagementRoute,
  component: UsersPage,
});

type Role = "admin" | "office_staff" | "driver";
type Status = "pending" | "active" | "inactive";
type Employment = "full_time" | "contractor" | "part_time";

const EMPLOYMENT_OPTIONS: { value: Employment; label: string }[] = [
  { value: "full_time", label: "正社員" },
  { value: "contractor", label: "業務委託" },
  { value: "part_time", label: "アルバイト" },
];

function employmentLabel(value: string): string {
  return EMPLOYMENT_OPTIONS.find((o) => o.value === value)?.label ?? "未設定";
}

export function UsersPage() {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [issued, setIssued] = useState<CreatedInviteDto | null>(null);
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [deleting, setDeleting] = useState<UserDto | null>(null);
  return (
    <MastersShell
      tab="users"
      createLabel="+ ユーザーを招待"
      searchPlaceholder="氏名で検索"
      onCreate={() => {
        setIssued(null);
        setInviteOpen(true);
      }}
    >
      <UsersTable onEdit={setEditing} onDelete={setDeleting} />
      {inviteOpen && (
        <InviteDialog issued={issued} onIssued={setIssued} onClose={() => setInviteOpen(false)} />
      )}
      {editing && <UserEditDialog row={editing} onClose={() => setEditing(null)} />}
      {deleting && <UserDeleteDialog row={deleting} onClose={() => setDeleting(null)} />}
    </MastersShell>
  );
}

function InviteDialog({
  issued,
  onIssued,
  onClose,
}: {
  issued: CreatedInviteDto | null;
  onIssued: (inv: CreatedInviteDto) => void;
  onClose: () => void;
}) {
  const api = useAdminApi();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);

  const inviteForm = useForm({
    defaultValues: {
      name: "",
      role: "driver" as Role,
    },
    onSubmit: async ({ value }) => {
      setFormError(null);
      try {
        const res = await api.invite.create({
          name: value.name.trim(),
          role: value.role,
        });
        onIssued(res.data);
        void queryClient.invalidateQueries({ queryKey: ["users"] });
        void queryClient.invalidateQueries({ queryKey: ["invites"] });
      } catch (err) {
        setFormError(formatApiError(err));
      }
    },
  });

  async function copyCode() {
    if (!issued) return;
    await copyToClipboard(issued.code, "招待コードをコピーしました");
  }

  if (issued) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 60,
        }}
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "var(--bg-panel)",
            border: "0.5px solid var(--rule)",
            borderRadius: 3,
            padding: 20,
            minWidth: 360,
            maxWidth: 440,
            fontSize: 13,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>招待コードを発行しました</h2>
            <Button type="button" variant="ghost" size="sm" aria-label="閉じる" onClick={onClose}>
              <X size={16} />
            </Button>
          </div>
          <p>
            <strong>{issued.user.name}</strong> 様の招待コードを発行しました。
            <br />
            下記コードをユーザーに共有してください (有効期限 24 時間)。
          </p>
          <div
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 22,
              letterSpacing: ".2em",
              padding: "10px 14px",
              background: "var(--bg-raised)",
              border: "0.5px solid var(--rule)",
              borderRadius: 2,
              textAlign: "center",
              margin: "12px 0",
            }}
          >
            {issued.code}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button type="button" variant="outline" size="sm" onClick={copyCode}>
              コピー
            </Button>
            <Button type="button" variant="default" size="sm" onClick={onClose}>
              閉じる
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <inviteForm.Subscribe selector={(s) => ({ isSubmitting: s.isSubmitting, values: s.values })}>
      {({ isSubmitting, values }) => (
        <FormDialog
          title="新規ユーザーを招待"
          submitLabel="招待コード発行"
          submitIcon={<UserPlus size={14} />}
          onClose={() => {
            inviteForm.reset();
            onClose();
          }}
          onSubmit={(e) => {
            e.preventDefault();
            void inviteForm.handleSubmit();
          }}
          submitting={isSubmitting}
          error={formError}
          disableSubmit={!values.name.trim()}
        >
          <Field label="氏名" required>
            <inviteForm.Field name="name">
              {(f) => (
                <Input
                  type="text"
                  value={f.state.value}
                  required
                  onChange={(e) => f.handleChange(e.target.value)}
                />
              )}
            </inviteForm.Field>
          </Field>
          <Field label="役割">
            <inviteForm.Field name="role">
              {(f) => (
                <Select value={f.state.value} onValueChange={(v) => f.handleChange(v as Role)}>
                  <SelectTrigger>
                    <SelectValue>
                      {(v: string | null) => (v ? roleLabel(v as Role) : "")}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="driver">ドライバー</SelectItem>
                    <SelectItem value="office_staff">事務所</SelectItem>
                    {session?.user?.role === "admin" && (
                      <SelectItem value="admin">管理者</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            </inviteForm.Field>
          </Field>
        </FormDialog>
      )}
    </inviteForm.Subscribe>
  );
}

function statusChipStyle(status: Status) {
  return {
    active: { bg: "var(--st-done-bg)", ink: "var(--st-done-ink)", label: "有効" },
    pending: { bg: "var(--st-assigned-bg)", ink: "var(--st-assigned-ink)", label: "招待中" },
    inactive: { bg: "var(--st-draft-bg)", ink: "var(--st-draft-ink)", label: "無効" },
  }[status];
}

function UsersTable({
  onEdit,
  onDelete,
}: {
  onEdit: (row: UserDto) => void;
  onDelete: (row: UserDto) => void;
}) {
  const q = useMastersQ();
  const { session } = useAuth();
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const [reissued, setReissued] = useState<{
    code: string;
    userName: string;
    expiresAt: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["users", "list", q, session?.user?.id],
    queryFn: () => api.user.list({ limit: 100, q: q || undefined }),
    enabled: !!session,
  });

  // 招待コード再発行 (招待中ユーザーのみ)。発行コードはこのモーダルでのみ表示し、閉じると再閲覧できない。
  const reissueMutation = useMutation({
    mutationFn: (userPublicId: string) => api.invite.reissue({ user_public_id: userPublicId }),
    onSuccess: (res) => {
      setReissued({
        code: res.data.code,
        userName: res.data.user.name,
        expiresAt: res.data.expires_at,
      });
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const rows = data?.data ?? [];
  const isOfficeStaff = session?.user?.role === "office_staff";

  function canEdit(row: UserDto) {
    return !isOfficeStaff || row.role !== "admin";
  }

  function canDelete(row: UserDto) {
    return canEdit(row) && (!isOfficeStaff || row.id !== session?.user?.id);
  }

  function canReissue(row: UserDto) {
    return row.status === "pending" && canDelete(row);
  }

  const columns: ColumnDef<UserDto>[] = [
    {
      accessorKey: "name",
      header: "氏名",
      cell: ({ getValue }) => <span style={{ fontWeight: 500 }}>{getValue<string>()}</span>,
    },
    {
      accessorKey: "role",
      header: "権限",
      size: 110,
      cell: ({ getValue }) => {
        const role = getValue<Role>();
        return (
          <span
            style={{
              fontSize: 11,
              padding: "1px 8px",
              border: "0.5px solid var(--rule-soft)",
              borderRadius: 2,
              fontFamily: "var(--f-mono)",
              color: role === "admin" ? "var(--accent)" : "var(--ink-2)",
            }}
          >
            {roleLabel(role)}
          </span>
        );
      },
    },
    {
      accessorKey: "status",
      header: "状態",
      size: 80,
      cell: ({ getValue }) => {
        const chip = statusChipStyle(getValue<Status>());
        return (
          <span className="chip" style={{ background: chip.bg, color: chip.ink, border: 0 }}>
            <span className="dot" />
            {chip.label}
          </span>
        );
      },
    },
    {
      accessorKey: "license_expires_at",
      header: "免許期限",
      size: 120,
      cell: ({ getValue }) => {
        const v = getValue<string | null>();
        if (!v) return <span style={{ color: "var(--ink-4)", fontSize: 11 }}></span>;
        const expired = new Date(v) < new Date();
        return (
          <span
            className="num"
            style={{ fontSize: 11, color: expired ? "var(--danger)" : "var(--ink-2)" }}
          >
            {v.slice(0, 10)}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      size: 220,
      cell: ({ row }) => (
        <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
          {canReissue(row.original) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => reissueMutation.mutate(row.original.id)}
              disabled={reissueMutation.isPending}
              style={{ fontSize: 11, padding: "3px 8px" }}
            >
              招待コード再発行
            </Button>
          )}
          <RowActionButtons
            onEdit={canEdit(row.original) ? () => onEdit(row.original) : undefined}
            onDelete={canDelete(row.original) ? () => onDelete(row.original) : undefined}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <MastersDataTable
        rows={rows}
        columns={columns}
        isLoading={isLoading}
        error={error}
        emptyMessage="該当するユーザーはありません"
        search={search}
      />
      {reissued && (
        <ReissuedDialog
          code={reissued.code}
          userName={reissued.userName}
          expiresAt={reissued.expiresAt}
          onClose={() => setReissued(null)}
        />
      )}
    </>
  );
}

function UserEditDialog({ row, onClose }: { row: UserDto; onClose: () => void }) {
  const api = useAdminApi();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  // チェックされた利用車両の public_id 集合。初期値は現状の利用車両から導出する。
  const [ownedVehicleIds, setOwnedVehicleIds] = useState<Set<string>>(() => new Set());
  const [ownedInitialized, setOwnedInitialized] = useState(false);
  // 他ユーザー利用中の車両を奪取する前に確認を挟むための対象。
  const [takeoverTarget, setTakeoverTarget] = useState<VehicleDto | null>(null);
  // 非ドライバー化で利用車両を解除する前の確認ダイアログ表示。
  const [demoteConfirm, setDemoteConfirm] = useState(false);
  // 解除確認済みフラグ (確認後の再 submit で確認を二重に出さない)。
  const demoteConfirmedRef = useRef(false);
  const isOfficeStaff = session?.user?.role === "office_staff";
  const isRestrictedSelf = isOfficeStaff && session?.user?.id === row.id;

  // 車両利用候補は役割に依らず読み込む (役割変更前後の昇格/降格どちらの保存にも対応するため)。
  const { data: vehiclesData } = useQuery({
    queryKey: ["vehicles", "all"],
    queryFn: () => api.vehicle.list({ limit: 100 }),
  });
  const vehicles = vehiclesData?.data ?? [];

  function toggleVehicle(publicId: string) {
    setOwnedVehicleIds((prev) => {
      const next = new Set(prev);
      if (next.has(publicId)) next.delete(publicId);
      else next.add(publicId);
      return next;
    });
  }

  // チェック状態が初期(=現状の利用)と異なる車両のみを更新する差分処理。
  // roleCanOwn=false (非ドライバー化) の場合はこのユーザーの利用車両をすべてリースに戻す。
  async function syncOwnedVehicles(roleCanOwn: boolean) {
    const updates = vehicles
      .map((v) => {
        const ownedNow = v.user_driver_id === row.id;
        const shouldOwn = roleCanOwn && ownedVehicleIds.has(v.id);
        if (ownedNow === shouldOwn) return null;
        return { vehicle: v, owner: shouldOwn ? row.id : null };
      })
      .filter((u): u is { vehicle: VehicleDto; owner: string | null } => u !== null);
    // 1件失敗しても残りの結果は保持する (差分処理のため再実行で冪等に回復できる)。
    // 失敗があれば件数を集計して throw し、呼出側の formError で通知する。
    const results = await Promise.allSettled(
      updates.map(({ vehicle, owner }) =>
        api.vehicle.update({
          publicId: vehicle.id,
          vehicle_number: vehicle.vehicle_number,
          vehicle_name: vehicle.vehicle_name,
          max_load_kg: vehicle.max_load_kg,
          user_driver_id: owner,
          width_cm: vehicle.width_cm,
          height_cm: vehicle.height_cm,
          depth_cm: vehicle.depth_cm,
          status: vehicle.status,
          remarks: vehicle.remarks,
        }),
      ),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      throw new Error(
        `利用車両の更新に一部失敗しました (${failed}/${updates.length} 件)。再度お試しください。`,
      );
    }
  }

  const editForm = useForm({
    defaultValues: {
      name: row.name,
      role: row.role,
      status: row.status,
      employmentType: row.employment_type ?? "",
      licenseExpiry: row.license_expires_at?.slice(0, 10) ?? "",
    },
    onSubmit: async ({ value }) => {
      setFormError(null);
      const submittedRole = isRestrictedSelf ? row.role : value.role;
      const submittedStatus = isRestrictedSelf ? row.status : value.status;
      const roleCanOwn = isDriverCapableRole(submittedRole);
      // 非ドライバー化でこのユーザーが車両を利用している場合は、利用解除前に確認を挟む。
      const willReleaseOwned = !roleCanOwn && vehicles.some((v) => v.user_driver_id === row.id);
      if (willReleaseOwned && !demoteConfirmedRef.current) {
        setDemoteConfirm(true);
        return;
      }
      try {
        await api.user.update({
          publicId: row.id,
          name: value.name.trim(),
          role: submittedRole,
          status: submittedStatus,
          employment_type: value.employmentType ? (value.employmentType as Employment) : null,
          license_expires_at: value.licenseExpiry || null,
        });
        // roleCanOwn=false なら利用車両をすべてリースに戻す。
        await syncOwnedVehicles(roleCanOwn);
        void queryClient.invalidateQueries({ queryKey: ["users"] });
        void queryClient.invalidateQueries({ queryKey: ["vehicles"] });
        toast.success("ユーザー情報を更新しました");
        onClose();
      } catch (err) {
        setFormError(formatApiError(err));
      } finally {
        demoteConfirmedRef.current = false;
      }
    },
  });

  // 役割はフォームの現在値ベースで判定する (編集中に driver↔office_staff を切り替えても追従)。
  const currentRole = useStore(editForm.store, (s) => s.values.role);
  const canOwnVehicles = isDriverCapableRole(currentRole);

  // 取得した車両一覧から、このユーザーが現在利用する車両を一度だけ初期選択へ反映する。
  if (canOwnVehicles && vehiclesData && !ownedInitialized) {
    setOwnedVehicleIds(
      new Set(vehicles.filter((v) => v.user_driver_id === row.id).map((v) => v.id)),
    );
    setOwnedInitialized(true);
  }

  return (
    <>
      {takeoverTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="利用者を変更"
          // #523: 編集ダイアログ (FormDialog, zIndex 60) の上に出す。同値だと後から
          // 描画される編集ダイアログの裏に隠れ、「画面が暗くなるだけ」に見えてしまう。
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
          }}
          onClick={() => setTakeoverTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--bg-panel)",
              border: "0.5px solid var(--rule)",
              borderRadius: 3,
              padding: 20,
              minWidth: 380,
              maxWidth: 480,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>利用者を変更</h2>
            <p style={{ fontSize: 12, color: "var(--ink-2)", margin: 0 }}>
              車両{" "}
              <strong>
                {takeoverTarget.vehicle_number}
                {takeoverTarget.vehicle_name ? ` (${takeoverTarget.vehicle_name})` : ""}
              </strong>{" "}
              は他のユーザーが利用しています。このユーザーの利用に変更しますか?
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setTakeoverTarget(null)}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => {
                  toggleVehicle(takeoverTarget.id);
                  setTakeoverTarget(null);
                }}
              >
                利用者を変更
              </Button>
            </div>
          </div>
        </div>
      )}
      {demoteConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="利用車両を解除しますか?"
          // #523: 編集ダイアログ (FormDialog, zIndex 60) の上に出す。
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
          }}
          onClick={() => setDemoteConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--bg-panel)",
              border: "0.5px solid var(--rule)",
              borderRadius: 3,
              padding: 20,
              minWidth: 380,
              maxWidth: 480,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>利用車両を解除しますか?</h2>
            <p style={{ fontSize: 12, color: "var(--ink-2)", margin: 0 }}>
              役割をドライバー以外に変更すると、このユーザーが利用する車両{" "}
              <strong>{vehicles.filter((v) => v.user_driver_id === row.id).length} 台</strong>{" "}
              がリース (共有) に戻ります。よろしいですか?
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDemoteConfirm(false)}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => {
                  demoteConfirmedRef.current = true;
                  setDemoteConfirm(false);
                  void editForm.handleSubmit();
                }}
              >
                解除して保存
              </Button>
            </div>
          </div>
        </div>
      )}
      <editForm.Subscribe selector={(s) => ({ isSubmitting: s.isSubmitting, values: s.values })}>
        {({ isSubmitting, values }) => (
          <FormDialog
            title="ユーザーを編集"
            submitLabel="更新"
            onClose={() => {
              editForm.reset();
              onClose();
            }}
            onSubmit={(e) => {
              e.preventDefault();
              void editForm.handleSubmit();
            }}
            submitting={isSubmitting}
            error={formError}
            disableSubmit={!values.name.trim()}
          >
            <Field label="氏名" required>
              <editForm.Field name="name">
                {(f) => (
                  <Input
                    type="text"
                    value={f.state.value}
                    required
                    onChange={(e) => f.handleChange(e.target.value)}
                  />
                )}
              </editForm.Field>
            </Field>
            <Field label="役割">
              <editForm.Field name="role">
                {(f) => (
                  <Select
                    value={f.state.value}
                    onValueChange={(v) => f.handleChange(v as Role)}
                    disabled={isRestrictedSelf}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {(v: string | null) => (v ? roleLabel(v as Role) : "")}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="driver">ドライバー</SelectItem>
                      <SelectItem value="office_staff">事務所</SelectItem>
                      {!isOfficeStaff && <SelectItem value="admin">管理者</SelectItem>}
                    </SelectContent>
                  </Select>
                )}
              </editForm.Field>
            </Field>
            <Field label="状態">
              <editForm.Field name="status">
                {(f) => (
                  <Select
                    value={f.state.value}
                    onValueChange={(v) => f.handleChange(v as Status)}
                    disabled={isRestrictedSelf}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {(v: string | null) =>
                          v === "pending"
                            ? "招待中"
                            : v === "active"
                              ? "有効"
                              : v === "inactive"
                                ? "無効"
                                : ""
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">招待中</SelectItem>
                      <SelectItem value="active">有効</SelectItem>
                      <SelectItem value="inactive">無効</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </editForm.Field>
            </Field>
            {canOwnVehicles && (
              <Field
                label="雇用形態"
                hint="運転日報の休憩算出に使用 (正社員は改善基準告示「430」を考慮)"
              >
                <editForm.Field name="employmentType">
                  {(f) => (
                    <Select
                      value={f.state.value || "unset"}
                      onValueChange={(v) => f.handleChange(v === "unset" ? "" : (v as Employment))}
                    >
                      <SelectTrigger>
                        <SelectValue>
                          {(v: string | null) =>
                            v && v !== "unset" ? employmentLabel(v) : "未設定"
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">未設定</SelectItem>
                        {EMPLOYMENT_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </editForm.Field>
              </Field>
            )}
            <Field label="免許証有効期限" hint="任意 (ドライバー稼働者向け)">
              <editForm.Field name="licenseExpiry">
                {(f) => (
                  <Input
                    type="date"
                    value={f.state.value}
                    onChange={(e) => f.handleChange(e.target.value)}
                  />
                )}
              </editForm.Field>
            </Field>
            {canOwnVehicles && !isRestrictedSelf && (
              <Field
                label="利用車両"
                hint="チェックした車両をこのユーザーの利用車両に設定 (外すとリースに戻す)"
              >
                <div
                  style={{
                    maxHeight: 160,
                    overflowY: "auto",
                    border: "0.5px solid var(--rule)",
                    borderRadius: 2,
                    padding: "4px 0",
                  }}
                >
                  {vehicles.length === 0 ? (
                    <div style={{ padding: "6px 10px", fontSize: 12, color: "var(--ink-4)" }}>
                      車両がありません
                    </div>
                  ) : (
                    vehicles.map((v) => {
                      const ownedByOther = v.user_driver_id !== null && v.user_driver_id !== row.id;
                      return (
                        <label
                          key={v.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "4px 10px",
                            fontSize: 12,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={ownedVehicleIds.has(v.id)}
                            onChange={() => {
                              const turningOn = !ownedVehicleIds.has(v.id);
                              // 他ユーザー利用中の車両を新たに利用させる時のみ確認を挟む。
                              if (turningOn && ownedByOther) setTakeoverTarget(v);
                              else toggleVehicle(v.id);
                            }}
                          />
                          <span style={{ fontWeight: 500 }}>{v.vehicle_number}</span>
                          {v.vehicle_name && (
                            <span style={{ color: "var(--ink-3)" }}>{v.vehicle_name}</span>
                          )}
                          {ownedByOther && (
                            <span
                              style={{ marginLeft: "auto", fontSize: 11, color: "var(--ink-4)" }}
                            >
                              他ユーザー利用中
                            </span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
              </Field>
            )}
          </FormDialog>
        )}
      </editForm.Subscribe>
    </>
  );
}

function UserDeleteDialog({ row, onClose }: { row: UserDto; onClose: () => void }) {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (publicId: string) => api.user.delete({ publicId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      void queryClient.invalidateQueries({ queryKey: ["invites"] });
      onClose();
    },
  });
  const error = mutation.error instanceof Error ? mutation.error.message : null;
  return (
    <ConfirmDeleteDialog
      title="ユーザーを削除"
      message={`ユーザー「${row.name}」を削除しますか?\nアカウントは無効化され、ログインできなくなります。`}
      onCancel={onClose}
      onConfirm={() => mutation.mutate(row.id)}
      submitting={mutation.isPending}
      error={error}
    />
  );
}

function ReissuedDialog({
  code,
  userName,
  expiresAt,
  onClose,
}: {
  code: string;
  userName: string;
  expiresAt: string;
  onClose: () => void;
}) {
  async function copy() {
    await copyToClipboard(code, "招待コードをコピーしました");
  }
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-panel)",
          border: "0.5px solid var(--rule)",
          borderRadius: 3,
          padding: 20,
          minWidth: 360,
          fontSize: 13,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>招待コードを再発行しました</h2>
          <Button type="button" variant="ghost" size="sm" aria-label="閉じる" onClick={onClose}>
            <X size={16} />
          </Button>
        </div>
        <p>
          <strong>{userName}</strong> 様の新しい招待コードです。
          <br />
          有効期限: {formatDateTime(expiresAt)}
        </p>
        <div
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 22,
            letterSpacing: ".2em",
            padding: "10px 14px",
            background: "var(--bg-raised)",
            border: "0.5px solid var(--rule)",
            borderRadius: 2,
            textAlign: "center",
            margin: "12px 0",
          }}
        >
          {code}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button type="button" variant="outline" size="sm" onClick={copy}>
            コピー
          </Button>
          <Button type="button" variant="default" size="sm" onClick={onClose}>
            閉じる
          </Button>
        </div>
      </div>
    </div>
  );
}
